import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtMoney, fmtErr } from "../../api";
import Layout from "../../components/Layout";
import { Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function InvoiceDetail() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [lines, setLines] = useState([]);
  const [newLine, setNewLine] = useState({ description: "", unit_price: "" });
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [chequeNum, setChequeNum] = useState("");
  const navigate = useNavigate();

  const [loadError, setLoadError] = useState(false);
  const load = () => api.get(`/invoices/${id}`).then((r) => { setInv(r.data); setLines(r.data.line_items || []); setLoadError(false); }).catch((e) => { setLoadError(true); toast.error(fmtErr(e)); });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when the route id changes; load is stable
  useEffect(() => { load(); }, [id]);

  if (loadError && !inv) return (
    <Layout title="Invoice">
      <div className="card-flat p-6 text-center" data-testid="invoice-load-error">
        <p className="font-bold">This invoice couldn't be opened</p>
        <p className="mt-1 text-sm text-muted-foreground">Its record may be incomplete. You can still mark it paid from the button below, or contact support.</p>
        <button data-testid="invoice-retry-btn" onClick={load} className="mt-4 h-12 w-full rounded-md border border-primary text-sm font-bold text-primary">Retry</button>
      </div>
    </Layout>
  );
  if (!inv) return <Layout title="Invoice"><p className="text-muted-foreground">Loading…</p></Layout>;

  const save = async () => {
    try { await api.patch(`/invoices/${id}`, { line_items: lines }); toast.success("Saved"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const send = async () => {
    await save();
    try { await api.post(`/invoices/${id}/send`); toast.success("Invoice sent"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const resend = async () => {
    try { await api.post(`/invoices/${id}/send`); toast.success("Invoice re-sent to the owner"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const markPaid = async (method, chequeNumber = "") => {
    try { await api.post(`/invoices/${id}/mark-paid`, { method, cheque_number: chequeNumber }); toast.success("Marked paid"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const addLine = () => {
    const price = Number(newLine.unit_price || 0);
    if (!newLine.description.trim() || !(price > 0)) return toast.error("Enter a description and a price above $0");
    setLines([...lines, { description: newLine.description.trim(), qty: 1, unit_price: price, amount: price, kind: "manual" }]);
    setNewLine({ description: "", unit_price: "" });
  };
  const cancel = async () => {
    if (!cancelReason.trim()) return toast.error("Give a reason — it's logged on the invoice");
    try { await api.post(`/invoices/${id}/cancel`, { reason: cancelReason.trim() }); toast.success("Invoice cancelled"); setShowCancel(false); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const del = async () => {
    try { await api.delete(`/invoices/${id}`); toast.success("Invoice deleted — removed from revenue and HST totals"); navigate("/invoices"); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <Layout title={inv.number}>
      <div className="space-y-6">
        <div className="card-flat p-6">
          {inv.business_name && <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground" data-testid="invoice-business">{inv.business_name}</p>}
          <p className="font-heading text-xl font-bold">{inv.property_name}</p>
          <p className="text-sm text-muted-foreground">{inv.owner_name}</p>
          {inv.billing?.name && (inv.billing.name !== inv.owner_name || (inv.billing.address && inv.billing.same_as_service === false)) && (
            <p className="mt-0.5 text-xs text-muted-foreground" data-testid="invoice-bill-to">
              Bill to: {inv.billing.name}{inv.billing.company ? ` — ${inv.billing.company}` : ""}{inv.billing.address ? ` · ${inv.billing.address}` : ""}
            </p>
          )}
          {inv.po_number && <p className="mt-0.5 text-xs font-semibold text-muted-foreground" data-testid="invoice-po">PO: {inv.po_number}</p>}
          <p className={`mt-2 inline-block rounded-full px-3 py-1 text-xs font-bold capitalize ${inv.status === "paid" ? "bg-primary text-primary-foreground" : ["overdue", "cancelled"].includes(inv.status) ? "bg-destructive text-destructive-foreground" : "bg-muted"}`} data-testid="invoice-status">{inv.status}</p>
          <a data-testid="download-pdf" href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.id}/pdf`} target="_blank" rel="noopener noreferrer"
            className="ml-3 inline-block text-sm font-bold text-primary underline">Download PDF</a>
        </div>

        {(inv.reminder_count > 0 || (inv.reminder_log || []).length > 0 || inv.resend_count > 0) && (
          <div className="card-flat p-6" data-testid="reminder-history">
            <p className="label-caps">Payment reminders</p>
            {inv.resend_count > 0 && (
              <p className="text-sm text-muted-foreground" data-testid="resend-info">
                Re-sent manually {inv.resend_count}× — last {new Date(inv.last_resent_at).toLocaleString()}
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              {(inv.reminder_count ?? inv.reminder_log?.length ?? 0)} emailed to {inv.owner_name}{inv.reminders_exhausted ? " — automatic reminders done, follow up personally" : ""}
            </p>
            <ul className="mt-2 space-y-1">
              {(inv.reminder_log || []).map((at, i) => (
                <li key={at} className="text-sm" data-testid={`reminder-entry-${i}`}>Reminder {i + 1} — {new Date(at).toLocaleString()}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="card-flat divide-y divide-border" data-testid="line-items">
          {lines.map((l, i) => (
            <div key={`${l.kind}-${l.description}-${l.amount}`} className="flex items-center justify-between gap-3 p-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{l.description}</p>
                {l.kind === "addon" && <span className="text-xs font-bold text-[hsl(43,74%,40%)]">ADD-ON</span>}
                {l.sub_cost != null && <p className="text-xs text-muted-foreground">Sub cost: {fmtMoney(l.sub_cost)}</p>}
              </div>
              {inv.status === "draft" ? (
                <div className="flex items-center gap-2">
                  {(l.qty || 1) > 1 && <span className="text-xs font-semibold text-muted-foreground">{l.qty} ×</span>}
                  <input data-testid={`line-price-${i}`} type="number" className="field-input w-28 text-right" value={l.unit_price}
                    onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, unit_price: Number(e.target.value), amount: Math.round((x.qty || 1) * Number(e.target.value) * 100) / 100 } : x))} />
                  {(l.qty || 1) > 1 && <span className="w-20 text-right text-sm font-bold" data-testid={`line-amount-${i}`}>{fmtMoney((l.qty || 1) * (l.unit_price || 0))}</span>}
                  <button data-testid={`line-del-${i}`} onClick={() => setLines(lines.filter((_, j) => j !== i))} className="shrink-0 text-destructive"><Trash size={18} weight="bold" /></button>
                </div>
              ) : (
                <span className="font-bold">{(l.qty || 1) > 1 ? `${l.qty} × ${fmtMoney(l.unit_price)} = ` : ""}{fmtMoney(l.amount)}</span>
              )}
            </div>
          ))}
          {inv.status === "draft" && (
            <div className="space-y-2 p-4" data-testid="add-line-row">
              <div className="flex items-center gap-2">
                <input data-testid="add-line-desc" className="field-input flex-1" placeholder="Add a line (e.g. garbage drop-off)" value={newLine.description} onChange={(e) => setNewLine({ ...newLine, description: e.target.value })} />
                <input data-testid="add-line-price" type="number" className="field-input w-24 text-right" placeholder="$" value={newLine.unit_price} onChange={(e) => setNewLine({ ...newLine, unit_price: e.target.value })} />
              </div>
              <button data-testid="add-line-btn" onClick={addLine} className="h-12 w-full rounded-md border border-primary text-sm font-bold text-primary">Add</button>
            </div>
          )}
          <div className="space-y-1 p-4">
            {(() => {
              const rate = inv.tax_rate_used ?? inv.hst_rate_used;
              const taxLabel = inv.tax_label_used || "HST";
              const taxNumber = inv.tax_number_used ?? inv.hst_number;
              const billable = lines.filter((l) => l.kind !== "credit");
              const creditLines = lines.filter((l) => l.kind === "credit");
              const sub = inv.status === "draft" ? billable.reduce((a, l) => a + (l.qty || 1) * (l.unit_price || 0), 0) : (inv.subtotal ?? inv.total);
              const cred = inv.status === "draft" ? creditLines.reduce((a, l) => a + (l.qty || 1) * (l.unit_price || 0), 0) : (inv.credits_total || 0);
              const taxable = Math.round((sub + cred) * 100) / 100;
              const tax = inv.status === "draft" ? (rate ? Math.round(taxable * rate) / 100 : 0) : (inv.tax_amount || 0);
              return (
                <>
                  <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span>{fmtMoney(sub)}</span></div>
                  {cred !== 0 && <div className="flex items-center justify-between text-sm" data-testid="credit-line"><span className="text-muted-foreground">Credits</span><span>{fmtMoney(cred)}</span></div>}
                  {cred !== 0 && <div className="flex items-center justify-between text-sm" data-testid="taxable-base"><span className="text-muted-foreground">Taxable amount</span><span>{fmtMoney(taxable)}</span></div>}
                  {tax > 0 && <div className="flex items-center justify-between text-sm" data-testid="tax-line"><span className="text-muted-foreground">{taxLabel} ({rate}%)</span><span>{fmtMoney(tax)}</span></div>}
                  <div className="flex items-center justify-between pt-1"><span className="label-caps">Total</span><span className="font-heading text-2xl font-bold" data-testid="invoice-total">{fmtMoney(inv.status === "draft" ? taxable + tax : inv.total)}</span></div>
                  {taxNumber && <p className="text-xs text-muted-foreground">{taxLabel} # {taxNumber}</p>}
                </>
              );
            })()}
          </div>
        </div>

        {inv.status === "draft" && (
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="save-draft-btn" onClick={save} className="h-14 rounded-md border border-border font-bold">Save draft</button>
            <button data-testid="send-invoice-btn" onClick={send} className="h-14 rounded-md bg-primary font-bold text-primary-foreground">Send invoice</button>
          </div>
        )}
        {["sent", "overdue"].includes(inv.status) && (
          <div className="space-y-3">
            <button data-testid="resend-invoice-btn" onClick={resend}
              title="Emails the whole owner household again with a fresh pay link — the previous link stops working"
              className="h-14 w-full rounded-md border border-primary font-bold text-primary">Re-send invoice</button>
            <button data-testid="mark-etransfer-btn" onClick={() => markPaid("etransfer")} className="btn-primary-big !h-14 !text-base">Mark received (e-Transfer)</button>
            <div className="flex gap-2">
              <input data-testid="cheque-number" className="field-input w-32" placeholder="Cheque #" value={chequeNum}
                onChange={(e) => setChequeNum(e.target.value)} />
              <button data-testid="mark-cheque-btn" onClick={() => markPaid("cheque", chequeNum)}
                className="h-14 flex-1 rounded-md border border-border font-bold">Mark paid (cheque)</button>
            </div>
            <button data-testid="mark-other-btn" onClick={() => markPaid("cash")} className="h-14 w-full rounded-md border border-border font-bold">Mark paid (cash/other)</button>
          </div>
        )}
        {inv.status === "paid" && (
          <div className="card-flat p-6 text-center" data-testid="paid-receipt">
            <p className="font-heading text-xl font-bold text-primary">Paid</p>
            <p className="text-sm text-muted-foreground">via {inv.payment_method}{inv.cheque_number ? ` #${inv.cheque_number}` : ""} · {new Date(inv.paid_at).toLocaleDateString()}</p>
          </div>
        )}
        {["draft", "sent", "overdue"].includes(inv.status) && (
          <div data-testid="cancel-invoice-section">
            {!showCancel ? (
              <button data-testid="cancel-invoice-btn" onClick={() => setShowCancel(true)} className="h-12 w-full rounded-md border border-destructive text-sm font-bold text-destructive">Cancel invoice</button>
            ) : (
              <div className="card-flat space-y-2 border-destructive/40 p-4" data-testid="cancel-invoice-form">
                <p className="text-xs text-muted-foreground">The invoice stays on record as cancelled, the pay link stops working, and linked jobs can be billed again.</p>
                <input data-testid="cancel-invoice-reason" className="field-input w-full" placeholder="Reason (e.g. work not performed)" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid="cancel-invoice-back" onClick={() => setShowCancel(false)} className="h-12 rounded-md border border-border text-sm font-bold">Keep invoice</button>
                  <button data-testid="cancel-invoice-confirm" onClick={cancel} className="h-12 rounded-md bg-destructive text-sm font-bold text-destructive-foreground">Cancel invoice</button>
                </div>
              </div>
            )}
          </div>
        )}
        {inv.status === "cancelled" && (
          <div className="card-flat border-l-4 border-l-destructive p-4" data-testid="invoice-cancelled-banner">
            <p className="font-bold text-destructive">Cancelled</p>
            <p className="text-sm text-muted-foreground">{inv.cancel_reason}{inv.cancelled_by ? ` — by ${inv.cancelled_by}` : ""}</p>
          </div>
        )}
        <div className="pt-2" data-testid="invoice-danger-zone">
          {!showDelete ? (
            <button data-testid="delete-invoice-btn" onClick={() => setShowDelete(true)} className="text-xs font-bold text-destructive underline underline-offset-2">Delete this invoice permanently</button>
          ) : (
            <div className="card-flat space-y-2 border-destructive/40 p-4" data-testid="delete-invoice-form">
              <p className="text-xs text-muted-foreground">Removes {inv.number} from the list and from revenue / HST totals. Linked jobs become billable again. This can't be undone — for test data and mistakes only.</p>
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="delete-invoice-back" onClick={() => setShowDelete(false)} className="h-12 rounded-md border border-border text-sm font-bold">Keep it</button>
                <button data-testid="delete-invoice-confirm" onClick={del} className="h-12 rounded-md bg-destructive text-sm font-bold text-destructive-foreground">Delete forever</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
